import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const ERROR_MESSAGES: Record<string, string> = {
  sso_cancelled: 'Sign-in was cancelled.',
  sso_failed: 'Sign-in failed, please try again.',
  sso_no_account: 'No account found for this Microsoft email. Contact your administrator.',
  sso_inactive: 'This account has been deactivated. Contact your administrator.',
};

const SsoCallback: React.FC = () => {
  const navigate = useNavigate();
  // This effect reads-then-clears window.location.hash, a one-shot side
  // effect that isn't safe to repeat. React.StrictMode double-invokes
  // effects in development, and the second pass would find the hash
  // already cleared by the first, falling through to the generic failure
  // path and clobbering a successful login. Guard so the real logic runs
  // exactly once regardless of StrictMode.
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    // Fragment is never sent to any server; scrub it immediately so it
    // doesn't linger in history/referrer.
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    window.history.replaceState(null, '', window.location.pathname);

    const params = new URLSearchParams(hash);
    const token = params.get('token');
    const userJson = params.get('user');
    const errorCode = params.get('error');

    if (token && userJson) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', userJson);
      navigate('/dashboard', { replace: true });
      return;
    }

    const message = errorCode
      ? ERROR_MESSAGES[errorCode] || 'Sign-in failed, please try again.'
      : 'Sign-in failed, please try again.';
    navigate('/login', { replace: true, state: { ssoError: message } });
  }, [navigate]);

  return (
    <div className="flex h-screen items-center justify-center bg-muted">
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Signing in" />
    </div>
  );
};

export default SsoCallback;
